import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import Toast from './Toast';
import { Button, Card, Input, Badge } from './UI';
import { Tab } from '@headlessui/react';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  CubeIcon,
  ChartBarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const InventoryManagement = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [receiveForm, setReceiveForm] = useState({
    quantity: '',
    reason: 'Purchase Order',
    notes: ''
  });

  const [adjustForm, setAdjustForm] = useState({
    quantity: '',
    adjustmentType: 'add',
    reason: 'Inventory Count',
    notes: ''
  });

  useEffect(() => {
    fetchInventoryProducts();
  }, []);

  const fetchInventoryProducts = async () => {
    try {
      const productsQuery = query(
        collection(db, 'products'),
        where('inventoryEnabled', '==', true)
      );
      const snapshot = await getDocs(productsQuery);
      const productsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching inventory products:', error);
      setLoading(false);
    }
  };

  const fetchProductTransactions = async (productId) => {
    try {
      const txQuery = query(
        collection(db, 'inventory_transactions'),
        where('productId', '==', productId),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(txQuery);
      const txList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTransactions(txList);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showToast('Failed to load transaction history', 'error');
    }
  };

  const handleReceiveStock = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;

    const quantity = parseInt(receiveForm.quantity);
    if (quantity <= 0) {
      showToast('Quantity must be greater than 0', 'error');
      return;
    }

    try {
      const productRef = doc(db, 'products', selectedProduct.id);

      await runTransaction(db, async (transaction) => {
        const productDoc = await transaction.get(productRef);
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }

        const currentStock = productDoc.data().stockQuantity || 0;
        const newStock = currentStock + quantity;

        transaction.update(productRef, {
          stockQuantity: newStock,
          updatedAt: serverTimestamp()
        });

        const txRef = doc(collection(db, 'inventory_transactions'));
        transaction.set(txRef, {
          productId: selectedProduct.id,
          productName: selectedProduct.name,
          type: 'receive',
          quantityChange: quantity,
          stockBefore: currentStock,
          stockAfter: newStock,
          reason: receiveForm.reason,
          notes: receiveForm.notes,
          performedBy: 'Admin',
          timestamp: serverTimestamp(),
          date: new Date().toISOString()
        });
      });

      showToast(`Successfully added ${quantity} units to ${selectedProduct.name}`, 'success');
      setReceiveForm({ quantity: '', reason: 'Purchase Order', notes: '' });
      fetchInventoryProducts();
      fetchProductTransactions(selectedProduct.id);
    } catch (error) {
      console.error('Error receiving stock:', error);
      showToast('Failed to receive stock', 'error');
    }
  };

  const handleAdjustStock = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;

    const quantity = parseInt(adjustForm.quantity);
    if (quantity <= 0) {
      showToast('Quantity must be greater than 0', 'error');
      return;
    }

    const quantityChange = adjustForm.adjustmentType === 'add' ? quantity : -quantity;

    try {
      const productRef = doc(db, 'products', selectedProduct.id);

      await runTransaction(db, async (transaction) => {
        const productDoc = await transaction.get(productRef);
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }

        const currentStock = productDoc.data().stockQuantity || 0;
        const newStock = Math.max(0, currentStock + quantityChange);

        transaction.update(productRef, {
          stockQuantity: newStock,
          updatedAt: serverTimestamp()
        });

        const txRef = doc(collection(db, 'inventory_transactions'));
        transaction.set(txRef, {
          productId: selectedProduct.id,
          productName: selectedProduct.name,
          type: 'adjustment',
          quantityChange: quantityChange,
          stockBefore: currentStock,
          stockAfter: newStock,
          reason: adjustForm.reason,
          notes: adjustForm.notes,
          performedBy: 'Admin',
          timestamp: serverTimestamp(),
          date: new Date().toISOString()
        });
      });

      showToast(`Successfully adjusted stock for ${selectedProduct.name}`, 'success');
      setAdjustForm({ quantity: '', adjustmentType: 'add', reason: 'Inventory Count', notes: '' });
      fetchInventoryProducts();
      fetchProductTransactions(selectedProduct.id);
    } catch (error) {
      console.error('Error adjusting stock:', error);
      showToast('Failed to adjust stock', 'error');
    }
  };

  const getLowStockProducts = () => {
    return products.filter(p => p.stockQuantity <= p.lowStockThreshold);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-500 mx-auto mb-4"></div>
          <p className="text-xl text-text-secondary">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Inventory Management</h1>
          <p className="text-text-secondary mt-1">Manage stock levels and track inventory movements</p>
        </div>

        {/* Low Stock Alert */}
        {getLowStockProducts().length > 0 && (
          <Card className="border-l-4 border-warning-500 bg-gradient-to-r from-warning-50 to-warning-100/50">
            <div className="flex items-start gap-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-warning-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-warning-900 mb-2">
                  ⚠️ Low Stock Alert
                </h3>
                <p className="text-warning-800 mb-3">
                  {getLowStockProducts().length} product(s) are running low on stock:
                </p>
                <div className="flex flex-wrap gap-2">
                  {getLowStockProducts().map(product => (
                    <Badge key={product.id} variant="warning" size="md">
                      {product.name} ({product.stockQuantity} left)
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Main Container */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Product List */}
          <div className="lg:col-span-1">
            <Card>
              <h2 className="text-xl font-bold text-text-primary mb-4">
                Inventory Products ({products.length})
              </h2>

              {products.length === 0 ? (
                <div className="text-center py-12">
                  <CubeIcon className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
                  <p className="text-text-muted">No products with inventory tracking enabled.</p>
                  <p className="text-sm text-text-muted mt-2">Enable inventory tracking in Product Management.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {products.map(product => {
                    const isOutOfStock = product.stockQuantity === 0;
                    const isLowStock = product.stockQuantity <= product.lowStockThreshold;
                    const isSelected = selectedProduct?.id === product.id;

                    return (
                      <div
                        key={product.id}
                        onClick={() => {
                          setSelectedProduct(product);
                          fetchProductTransactions(product.id);
                        }}
                        className={`
                          p-4 rounded-lg border-2 cursor-pointer transition-all
                          ${isSelected
                            ? 'border-primary-500 bg-primary-50 shadow-md'
                            : 'border-neutral-200 bg-white hover:border-primary-300 hover:shadow-sm'
                          }
                        `}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <strong className="text-text-primary font-semibold">{product.name}</strong>
                          {isOutOfStock ? (
                            <Badge variant="danger" size="sm">Out of Stock</Badge>
                          ) : isLowStock ? (
                            <Badge variant="warning" size="sm">Low Stock</Badge>
                          ) : (
                            <Badge variant="success" size="sm">In Stock</Badge>
                          )}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-2xl font-bold text-primary-600">
                            {product.stockQuantity}
                          </span>
                          <span className="text-text-muted">
                            Alert at: {product.lowStockThreshold}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          {/* Right Panel - Operations */}
          <div className="lg:col-span-2">
            <Card>
              {!selectedProduct ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <CubeIcon className="h-20 w-20 text-neutral-300 mb-4" />
                  <p className="text-xl text-text-muted">Select a product to manage inventory</p>
                </div>
              ) : (
                <>
                  {/* Selected Product Header */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-neutral-200">
                    <h2 className="text-2xl font-bold text-text-primary">{selectedProduct.name}</h2>
                    <div className="text-right">
                      <p className="text-sm text-text-muted">Current Stock</p>
                      <p className="text-3xl font-bold text-primary-600">{selectedProduct.stockQuantity}</p>
                    </div>
                  </div>

                  {/* Tabs */}
                  <Tab.Group>
                    <Tab.List className="flex space-x-1 rounded-xl bg-neutral-100 p-1 mb-6">
                      {['Overview', 'Receive Stock', 'Adjust Stock', 'History'].map((category) => (
                        <Tab
                          key={category}
                          className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all
                            ${selected
                              ? 'bg-white text-primary-700 shadow'
                              : 'text-text-secondary hover:bg-white/50 hover:text-text-primary'
                            }`
                          }
                        >
                          {category}
                        </Tab>
                      ))}
                    </Tab.List>

                    <Tab.Panels>
                      {/* Overview Tab */}
                      <Tab.Panel>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="p-6 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200">
                            <div className="flex items-center gap-3 mb-2">
                              <CubeIcon className="h-6 w-6 text-primary-600" />
                              <p className="text-sm font-medium text-primary-900">Current Stock</p>
                            </div>
                            <p className="text-3xl font-bold text-primary-600">{selectedProduct.stockQuantity}</p>
                          </div>

                          <div className="p-6 bg-gradient-to-br from-warning-50 to-warning-100 rounded-xl border border-warning-200">
                            <div className="flex items-center gap-3 mb-2">
                              <ExclamationTriangleIcon className="h-6 w-6 text-warning-600" />
                              <p className="text-sm font-medium text-warning-900">Low Stock Threshold</p>
                            </div>
                            <p className="text-3xl font-bold text-warning-600">{selectedProduct.lowStockThreshold}</p>
                          </div>

                          <div className="p-6 bg-gradient-to-br from-success-50 to-success-100 rounded-xl border border-success-200">
                            <div className="flex items-center gap-3 mb-2">
                              <ChartBarIcon className="h-6 w-6 text-success-600" />
                              <p className="text-sm font-medium text-success-900">Price</p>
                            </div>
                            <p className="text-3xl font-bold text-success-600">MVR {selectedProduct.price.toFixed(2)}</p>
                          </div>

                          <div className="p-6 bg-gradient-to-br from-info-50 to-info-100 rounded-xl border border-info-200">
                            <div className="flex items-center gap-3 mb-2">
                              <CheckCircleIcon className="h-6 w-6 text-info-600" />
                              <p className="text-sm font-medium text-info-900">Status</p>
                            </div>
                            <p className="text-xl font-bold text-info-600">
                              {selectedProduct.stockQuantity === 0 ? 'Out of Stock' :
                               selectedProduct.stockQuantity <= selectedProduct.lowStockThreshold ? 'Low Stock' : 'In Stock'}
                            </p>
                          </div>
                        </div>
                      </Tab.Panel>

                      {/* Receive Stock Tab */}
                      <Tab.Panel>
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-bold text-text-primary mb-1">Receive Stock</h3>
                            <p className="text-sm text-text-muted">Add new stock to inventory (purchases, deliveries, etc.)</p>
                          </div>

                          <form onSubmit={handleReceiveStock} className="space-y-4">
                            <Input
                              label="Quantity to Receive"
                              type="number"
                              value={receiveForm.quantity}
                              onChange={(e) => setReceiveForm({...receiveForm, quantity: e.target.value})}
                              min="1"
                              required
                              placeholder="Enter quantity"
                            />

                            <div>
                              <label className="block text-sm font-medium text-text-secondary mb-2">Reason</label>
                              <select
                                value={receiveForm.reason}
                                onChange={(e) => setReceiveForm({...receiveForm, reason: e.target.value})}
                                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all"
                              >
                                <option value="Purchase Order">Purchase Order</option>
                                <option value="Supplier Delivery">Supplier Delivery</option>
                                <option value="Return from Customer">Return from Customer</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-text-secondary mb-2">Notes (Optional)</label>
                              <textarea
                                value={receiveForm.notes}
                                onChange={(e) => setReceiveForm({...receiveForm, notes: e.target.value})}
                                rows="3"
                                placeholder="Add any additional notes..."
                                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all resize-none"
                              />
                            </div>

                            <Button type="submit" variant="success" size="lg" fullWidth>
                              <PlusIcon className="h-5 w-5" />
                              Receive Stock
                            </Button>
                          </form>
                        </div>
                      </Tab.Panel>

                      {/* Adjust Stock Tab */}
                      <Tab.Panel>
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-bold text-text-primary mb-1">Adjust Stock</h3>
                            <p className="text-sm text-text-muted">Make manual adjustments (corrections, damages, etc.)</p>
                          </div>

                          <form onSubmit={handleAdjustStock} className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-text-secondary mb-3">Adjustment Type</label>
                              <div className="grid grid-cols-2 gap-3">
                                <label className={`
                                  flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all
                                  ${adjustForm.adjustmentType === 'add'
                                    ? 'border-success-500 bg-success-50 text-success-700'
                                    : 'border-neutral-200 bg-white text-text-secondary hover:border-success-300'
                                  }
                                `}>
                                  <input
                                    type="radio"
                                    value="add"
                                    checked={adjustForm.adjustmentType === 'add'}
                                    onChange={(e) => setAdjustForm({...adjustForm, adjustmentType: e.target.value})}
                                    className="sr-only"
                                  />
                                  <ArrowUpIcon className="h-5 w-5" />
                                  <span className="font-semibold">Add Stock</span>
                                </label>

                                <label className={`
                                  flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all
                                  ${adjustForm.adjustmentType === 'subtract'
                                    ? 'border-danger-500 bg-danger-50 text-danger-700'
                                    : 'border-neutral-200 bg-white text-text-secondary hover:border-danger-300'
                                  }
                                `}>
                                  <input
                                    type="radio"
                                    value="subtract"
                                    checked={adjustForm.adjustmentType === 'subtract'}
                                    onChange={(e) => setAdjustForm({...adjustForm, adjustmentType: e.target.value})}
                                    className="sr-only"
                                  />
                                  <ArrowDownIcon className="h-5 w-5" />
                                  <span className="font-semibold">Subtract Stock</span>
                                </label>
                              </div>
                            </div>

                            <Input
                              label="Quantity"
                              type="number"
                              value={adjustForm.quantity}
                              onChange={(e) => setAdjustForm({...adjustForm, quantity: e.target.value})}
                              min="1"
                              required
                              placeholder="Enter quantity"
                            />

                            <div>
                              <label className="block text-sm font-medium text-text-secondary mb-2">Reason</label>
                              <select
                                value={adjustForm.reason}
                                onChange={(e) => setAdjustForm({...adjustForm, reason: e.target.value})}
                                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all"
                              >
                                <option value="Inventory Count">Inventory Count</option>
                                <option value="Damaged Goods">Damaged Goods</option>
                                <option value="Theft/Loss">Theft/Loss</option>
                                <option value="Expired">Expired</option>
                                <option value="Correction">Correction</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-text-secondary mb-2">Notes (Optional)</label>
                              <textarea
                                value={adjustForm.notes}
                                onChange={(e) => setAdjustForm({...adjustForm, notes: e.target.value})}
                                rows="3"
                                placeholder="Add any additional notes..."
                                className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all resize-none"
                              />
                            </div>

                            <Button
                              type="submit"
                              variant={adjustForm.adjustmentType === 'add' ? 'success' : 'danger'}
                              size="lg"
                              fullWidth
                            >
                              {adjustForm.adjustmentType === 'add' ? (
                                <>
                                  <ArrowUpIcon className="h-5 w-5" />
                                  Add Stock
                                </>
                              ) : (
                                <>
                                  <ArrowDownIcon className="h-5 w-5" />
                                  Subtract Stock
                                </>
                              )}
                            </Button>
                          </form>
                        </div>
                      </Tab.Panel>

                      {/* History Tab */}
                      <Tab.Panel>
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-bold text-text-primary mb-1">Transaction History</h3>
                            <p className="text-sm text-text-muted">Last 50 transactions for this product</p>
                          </div>

                          {transactions.length === 0 ? (
                            <div className="text-center py-12">
                              <ClockIcon className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
                              <p className="text-text-muted">No transactions recorded yet.</p>
                            </div>
                          ) : (
                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                              {transactions.map(tx => (
                                <div key={tx.id} className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                                  <div className="flex items-start justify-between mb-3">
                                    <Badge
                                      variant={tx.type === 'receive' ? 'success' : 'info'}
                                      size="sm"
                                    >
                                      {tx.type.toUpperCase()}
                                    </Badge>
                                    <span className="text-sm text-text-muted flex items-center gap-1">
                                      <ClockIcon className="h-4 w-4" />
                                      {formatDate(tx.date)}
                                    </span>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                      <span className={`text-lg font-bold ${tx.quantityChange > 0 ? 'text-success-600' : 'text-danger-600'}`}>
                                        {tx.quantityChange > 0 ? '+' : ''}{tx.quantityChange} units
                                      </span>
                                      <span className="text-sm text-text-secondary font-medium">
                                        {tx.stockBefore} → {tx.stockAfter}
                                      </span>
                                    </div>

                                    <div className="text-sm">
                                      <strong className="text-text-primary">Reason:</strong>
                                      <span className="text-text-secondary ml-2">{tx.reason}</span>
                                    </div>

                                    {tx.notes && (
                                      <div className="text-sm">
                                        <strong className="text-text-primary">Notes:</strong>
                                        <span className="text-text-secondary ml-2">{tx.notes}</span>
                                      </div>
                                    )}

                                    <div className="text-xs text-text-muted pt-2 border-t border-neutral-200">
                                      Performed by: {tx.performedBy}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Tab.Panel>
                    </Tab.Panels>
                  </Tab.Group>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default InventoryManagement;
